import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Ban, BadgeCheck, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import {
  useBonusPaidMutation,
  useCancelBonusMutation,
  useCancelCompChangeMutation,
  useCompHistoryQuery,
  useProposeBonusMutation,
  useProposeCompChangeMutation,
} from '@/client/api/hr/comp';
import {
  BAND_BADGE,
  BONUS_KIND_LABEL,
  COMP_REASON_LABEL,
  COMP_STATUS_BADGE,
  type BonusKind,
  type CompReason,
  type Employee,
  type HrLevel,
  type PayFrequency,
  type SalaryView,
} from '@/client/types/hr';
import { DeleteConfirmDialog, RowMenu, StatusBadge } from './hr-ui';
import { money, pay, salary } from './comp-format';
import { formatDay } from './leave-format';

const inputClass =
  'h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm outline-none ring-blue-500 focus:ring-2';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      {children}
    </label>
  );
}

const today = () => new Date().toISOString().slice(0, 10);

/** Record existing pay (`comp.approve`, approved on entry) or propose a change
 *  (`comp.manage`, waits for someone else to approve). */
export function CompChangeDialog({
  open,
  mode,
  employee,
  current,
  levels,
  reviewId,
  onClose,
}: {
  open: boolean;
  mode: 'record' | 'propose';
  employee: Employee | null;
  current: SalaryView | null;
  levels: HrLevel[];
  /** Set when proposing from a performance review: the change links back to it. */
  reviewId?: string | null;
  onClose: () => void;
}) {
  const save = useProposeCompChangeMutation();
  const reasons: CompReason[] = mode === 'record' ? ['hire', 'correction'] : ['raise', 'promotion', 'adjustment'];
  const [form, setForm] = useState({
    reason: reasons[0],
    amount: '',
    currency: 'GHS',
    frequency: 'monthly' as PayFrequency,
    effectiveOn: today(),
    levelId: '',
    note: '',
  });

  useEffect(() => {
    if (!open) return;
    setForm({
      reason: (mode === 'record' ? (current ? 'correction' : 'hire') : 'raise') as CompReason,
      amount: current ? String(current.amount) : '',
      currency: current?.currency ?? 'GHS',
      frequency: current?.frequency ?? 'monthly',
      effectiveOn: mode === 'record' ? employee?.startDate?.slice(0, 10) || today() : today(),
      levelId: '',
      note: '',
    });
  }, [open, mode, current, employee]);

  const level = levels.find((l) => l.id === (form.reason === 'promotion' ? form.levelId : employee?.levelId));
  const amount = Number(form.amount);
  const monthly = form.frequency === 'annual' ? amount / 12 : form.frequency === 'monthly' ? amount : null;
  const band = level?.compensationBand;
  const bandHint =
    band && monthly && band.currency === form.currency.toUpperCase()
      ? monthly < band.minMonthly
        ? `Below the ${level?.code} band (${money(band.minMonthly, band.currency)} – ${money(band.maxMonthly, band.currency)} per month)`
        : monthly > band.maxMonthly
          ? `Above the ${level?.code} band (${money(band.minMonthly, band.currency)} – ${money(band.maxMonthly, band.currency)} per month)`
          : `Within the ${level?.code} band`
      : null;
  const change = current && amount && current.currency === form.currency.toUpperCase() && current.frequency === form.frequency
    ? ((amount - current.amount) / current.amount) * 100
    : null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!employee) return;
    if (!amount || amount <= 0) {
      toast.error('Enter an amount above 0');
      return;
    }
    if (form.reason === 'promotion' && !form.levelId) {
      toast.error('Pick the level they are promoted to');
      return;
    }
    save.mutate(
      {
        record: mode === 'record',
        employeeId: employee.id,
        reason: form.reason,
        amount,
        currency: form.currency.trim().toUpperCase(),
        frequency: form.frequency,
        effectiveOn: form.effectiveOn,
        levelId: form.reason === 'promotion' ? form.levelId : null,
        reviewId: reviewId ?? null,
        note: form.note.trim(),
      },
      {
        onSuccess: (res) => {
          toast.success(mode === 'record' ? 'Pay recorded' : 'Proposed. Someone with Approve pay changes now has to approve it.');
          if (res.band && res.band !== 'within') toast.warning(BAND_BADGE[res.band].label);
          onClose();
        },
        onError: (err) => toast.error((err as Error).message || 'Could not save'),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {mode === 'record' ? 'Record pay' : 'Propose a pay change'}
            {employee ? ` · ${employee.name}` : ''}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-xs text-gray-500">
            {mode === 'record'
              ? 'For pay that already exists: their starting pay, or fixing a wrong entry. Saved as approved.'
              : `Current pay: ${salary(current)}. Someone other than you approves this.`}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Reason">
              <select
                value={form.reason}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value as CompReason }))}
                className={inputClass}
              >
                {reasons.map((r) => (
                  <option key={r} value={r}>
                    {COMP_REASON_LABEL[r]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Effective from">
              <input
                type="date"
                value={form.effectiveOn}
                onChange={(e) => setForm((f) => ({ ...f, effectiveOn: e.target.value }))}
                className={inputClass}
              />
            </Field>
          </div>
          {form.reason === 'promotion' && (
            <Field label="New level">
              <select value={form.levelId} onChange={(e) => setForm((f) => ({ ...f, levelId: e.target.value }))} className={inputClass}>
                <option value="">Select a level</option>
                {levels
                  .filter((l) => l.status === 'active' && l.id !== employee?.levelId)
                  .map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.code} · {l.name}
                    </option>
                  ))}
              </select>
            </Field>
          )}
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Base pay">
              <input
                type="number"
                min={0}
                step="0.01"
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field label="Currency">
              <input
                value={form.currency}
                maxLength={3}
                onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))}
                className={inputClass}
              />
            </Field>
            <Field label="Per">
              <select
                value={form.frequency}
                onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value as PayFrequency }))}
                className={inputClass}
              >
                <option value="monthly">Month</option>
                <option value="annual">Year</option>
                <option value="hourly">Hour</option>
              </select>
            </Field>
          </div>
          {reviewId && <p className="text-xs text-gray-500">Linked to their performance review.</p>}
          {(bandHint || change !== null) && (
            <p className={`text-xs ${bandHint && !bandHint.startsWith('Within') ? 'text-amber-700' : 'text-gray-500'}`}>
              {change !== null && mode === 'propose' && `${change >= 0 ? '+' : ''}${change.toFixed(1)}% on current pay. `}
              {bandHint}
            </p>
          )}
          <Field label="Note (why)">
            <textarea
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              rows={2}
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm outline-none ring-blue-500 focus:ring-2"
            />
          </Field>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              {mode === 'record' ? 'Record' : 'Propose'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function BonusDialog({
  open,
  employee,
  reviewId,
  onClose,
}: {
  open: boolean;
  employee: Employee | null;
  reviewId?: string | null;
  onClose: () => void;
}) {
  const save = useProposeBonusMutation();
  const [form, setForm] = useState({ kind: 'performance' as BonusKind, amount: '', currency: 'GHS', payableOn: '', note: '' });
  useEffect(() => {
    if (open) setForm({ kind: 'performance', amount: '', currency: 'GHS', payableOn: '', note: '' });
  }, [open]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!employee) return;
    const amount = Number(form.amount);
    if (!amount || amount <= 0) {
      toast.error('Enter an amount above 0');
      return;
    }
    save.mutate(
      {
        employeeId: employee.id,
        kind: form.kind,
        amount,
        currency: form.currency.trim().toUpperCase(),
        payableOn: form.payableOn || null,
        reviewId: reviewId ?? null,
        note: form.note.trim(),
      },
      {
        onSuccess: () => {
          toast.success('Bonus proposed. Someone else has to approve it.');
          onClose();
        },
        onError: (err) => toast.error((err as Error).message || 'Could not propose the bonus'),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Propose a bonus{employee ? ` · ${employee.name}` : ''}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Kind">
              <select value={form.kind} onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value as BonusKind }))} className={inputClass}>
                {Object.entries(BONUS_KIND_LABEL).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Pay on (optional)">
              <input type="date" value={form.payableOn} onChange={(e) => setForm((f) => ({ ...f, payableOn: e.target.value }))} className={inputClass} />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Field label="Amount">
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                  className={inputClass}
                />
              </Field>
            </div>
            <Field label="Currency">
              <input value={form.currency} maxLength={3} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} className={inputClass} />
            </Field>
          </div>
          <Field label="Note (why)">
            <textarea
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              rows={2}
              className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm outline-none ring-blue-500 focus:ring-2"
            />
          </Field>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
              Propose
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** One person's full pay history, with the follow-up actions on each item. */
export function CompHistoryDialog({ employee, onClose }: { employee: Employee | null; onClose: () => void }) {
  const historyQ = useCompHistoryQuery(employee?.id, Boolean(employee));
  const canManage = useGate(PERMISSIONS.comp.manage).allowed;
  const cancelChange = useCancelCompChangeMutation();
  const cancelBonus = useCancelBonusMutation();
  const paid = useBonusPaidMutation();
  const [pending, setPending] = useState<{ kind: 'cancelChange' | 'cancelBonus' | 'paid'; id: string; label: string } | null>(null);
  const h = historyQ.data;

  const timeline = useMemo(() => h?.changes ?? [], [h]);

  const run = () => {
    if (!pending) return;
    const done = (msg: string) => ({ onSuccess: () => toast.success(msg), onError: (e: unknown) => toast.error((e as Error).message) });
    if (pending.kind === 'cancelChange') cancelChange.mutate(pending.id, done('Proposal withdrawn'));
    if (pending.kind === 'cancelBonus') cancelBonus.mutate(pending.id, done('Bonus withdrawn'));
    if (pending.kind === 'paid') paid.mutate(pending.id, done('Marked paid'));
    setPending(null);
  };

  return (
    <Dialog open={!!employee} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Pay history{employee ? ` · ${employee.name}` : ''}</DialogTitle>
        </DialogHeader>
        {historyQ.isLoading ? (
          <p className="flex items-center gap-2 py-8 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading pay history…
          </p>
        ) : historyQ.isError || !h ? (
          <p className="py-8 text-sm text-red-600">Could not load pay history: {(historyQ.error as Error)?.message}</p>
        ) : (
          <div className="space-y-5">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs text-gray-500">Current pay</p>
                <p className="text-lg font-semibold text-gray-900">{salary(h.current)}</p>
                {h.current && <p className="text-xs text-gray-400">since {formatDay(h.current.effectiveOn)}</p>}
                {h.band && <StatusBadge label={BAND_BADGE[h.band].label} className={`mt-1 ${BAND_BADGE[h.band].className}`} />}
              </div>
              <div className="rounded-lg border border-gray-100 p-3">
                <p className="text-xs text-gray-500">Scheduled</p>
                <p className="text-lg font-semibold text-gray-900">{salary(h.scheduled)}</p>
                {h.scheduled && <p className="text-xs text-gray-400">from {formatDay(h.scheduled.effectiveOn)}</p>}
              </div>
            </div>

            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">Base pay changes</p>
              {timeline.length === 0 ? (
                <p className="text-sm text-gray-500">No pay recorded yet.</p>
              ) : (
                <ul className="divide-y divide-gray-50">
                  {timeline.map((c) => (
                    <li key={c.id} className="flex items-start justify-between gap-3 py-2">
                      <div className="min-w-0">
                        <p className="text-sm text-gray-900">
                          {pay(c.amount, c.currency, c.frequency)} · {COMP_REASON_LABEL[c.reason]}
                          {c.recorded && <span className="text-xs text-gray-400"> (recorded)</span>}
                        </p>
                        <p className="text-xs text-gray-500">From {formatDay(c.effectiveOn)}</p>
                        {c.note && <p className="text-xs text-gray-500">“{c.note}”</p>}
                        {c.decisionNote && <p className="text-xs text-gray-500">Decision: “{c.decisionNote}”</p>}
                      </div>
                      <div className="flex items-center gap-1">
                        <StatusBadge label={c.status} className={`capitalize ${COMP_STATUS_BADGE[c.status]}`} />
                        <RowMenu
                          items={[
                            {
                              label: 'Withdraw proposal',
                              icon: Ban,
                              danger: true,
                              hidden: !canManage || c.status !== 'proposed',
                              onSelect: () => setPending({ kind: 'cancelChange', id: c.id, label: COMP_REASON_LABEL[c.reason] }),
                            },
                          ]}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-400">Bonuses</p>
              {h.bonuses.length === 0 ? (
                <p className="text-sm text-gray-500">No bonuses.</p>
              ) : (
                <ul className="divide-y divide-gray-50">
                  {h.bonuses.map((b) => (
                    <li key={b.id} className="flex items-start justify-between gap-3 py-2">
                      <div className="min-w-0">
                        <p className="text-sm text-gray-900">
                          {money(b.amount, b.currency)} · {BONUS_KIND_LABEL[b.kind]}
                        </p>
                        <p className="text-xs text-gray-500">
                          {b.payableOn ? `Pay on ${formatDay(b.payableOn)}` : 'No pay date'}
                          {b.paidAt && ` · paid ${formatDay(b.paidAt)}`}
                        </p>
                        {b.note && <p className="text-xs text-gray-500">“{b.note}”</p>}
                      </div>
                      <div className="flex items-center gap-1">
                        <StatusBadge label={b.status} className={`capitalize ${COMP_STATUS_BADGE[b.status]}`} />
                        <RowMenu
                          items={[
                            {
                              label: 'Mark paid',
                              icon: BadgeCheck,
                              hidden: !canManage || b.status !== 'approved',
                              onSelect: () => setPending({ kind: 'paid', id: b.id, label: money(b.amount, b.currency) }),
                            },
                            {
                              label: 'Withdraw proposal',
                              icon: Ban,
                              danger: true,
                              hidden: !canManage || b.status !== 'proposed',
                              onSelect: () => setPending({ kind: 'cancelBonus', id: b.id, label: money(b.amount, b.currency) }),
                            },
                          ]}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
        <DeleteConfirmDialog
          open={!!pending}
          onOpenChange={(v) => !v && setPending(null)}
          title={pending?.kind === 'paid' ? 'Mark this bonus paid?' : 'Withdraw this proposal?'}
          description={
            pending?.kind === 'paid'
              ? `${pending.label}. Do this once the money has gone out. Payroll will do it automatically once it is live.`
              : `${pending?.label ?? ''}. It is kept in the history as cancelled.`
          }
          actionLabel={pending?.kind === 'paid' ? 'Mark paid' : 'Withdraw'}
          danger={pending?.kind !== 'paid'}
          onConfirm={run}
        />
      </DialogContent>
    </Dialog>
  );
}
